import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MaskdInput from 'react-text-mask';

import { Cookie } from '../../../tscapi/utils/cookie';
import { openHelpPanel } from '../utils/openHelpPanel';

import { en, fr } from './i18n';

export type ContactFormType = 'general' | 'billing' | 'feedback';

export interface ContactFormProps {
    title: string;
    subtitle: string | JSX.Element;
    type?: ContactFormType;
}

declare global {
    interface Window {
        salesForceWebToCaseURL: string;
        orgId: string;
        orderNumberParam: string;
    }
}

const ContactForm = ({ title, subtitle, type = 'general' }: ContactFormProps) => {
    const cookie: Cookie = new Cookie();
    const { i18n, t } = useTranslation('customer-service');
    i18n.addResourceBundle('en', 'customer-service', en, true, true);
    i18n.addResourceBundle('fr', 'customer-service', fr, true, true);

    const [isSubmitSuccess, setIsSubmitSuccess] = useState<boolean>(true);
    const [step, setStep] = useState<string>('before submit');

    const [email, setEmail] = useState<string>('');
    const [orderNo, setOrderNo] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');

    const [phone, setPhone] = useState<string>('');
    const [concern, setConcern] = useState<string>('');

    const [isSubmitClicked, setIsSubmitClicked] = useState<boolean>(false);

    const [isValidAll, setIsValidAll] = useState<boolean>(true);
    const [isValidEmail, setIsValidEmail] = useState<boolean>(true);
    const [isValidOrderNo, setIsValidOrderNo] = useState<boolean>(true);
    const [isValidFirstName, setIsValidFirstName] = useState<boolean>(true);
    const [isValidLastName, setIsValidLastName] = useState<boolean>(true);
    const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
    const [isValidConcern, setIsValidConcern] = useState<boolean>(true);

    const getIsEmailValid = () => {
        return email != '' && /^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]{2,5})$/.test(email);
    };

    const onEmailAddressBlur = () => {
        setIsValidEmail(getIsEmailValid());
    };

    const onEmailAddressFocus = () => {
        setIsValidEmail(true);
    };

    const getIsOrderNoValid = () => {
        return orderNo != '' && /^[a-zA-Z]{1}[0-9]{7,11}$/.test(orderNo);
    };

    const onOrderNoBlur = () => {
        setOrderNo(orderNo.toUpperCase());
        setIsValidOrderNo(getIsOrderNoValid());
    };

    const onOrderNoFocus = () => {
        setIsValidOrderNo(true);
    };

    const getIsFirstNameValid = () => {
        return firstName != '' && /^[a-zA-Z \-àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ]{1,80}$/.test(firstName);
    };

    const onFirstNameBlur = () => {
        setIsValidFirstName(getIsFirstNameValid());
    };

    const onFirstNameFocus = () => {
        setIsValidFirstName(true);
    };

    const getIsLastNameValid = () => {
        return lastName != '' && /^[a-zA-Z \-àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ]{1,80}$/.test(lastName);
    };

    const onLastNameBlur = () => {
        setIsValidLastName(getIsLastNameValid());
    };

    const onLastNameFocus = () => {
        setIsValidLastName(true);
    };

    const getIsPhoneValid = () => {
        return phone != '' && /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/.test(phone);
    };

    const onPhoneBlur = () => {
        setIsValidPhone(getIsPhoneValid());
    };

    const onPhoneFocus = () => {
        setIsValidPhone(true);
    };

    const getIsConcernValid = () => {
        return concern != '';
    };

    const onConcernBlur = () => {
        setIsValidConcern(getIsConcernValid());
    };

    const onConcernFocus = () => {
        setIsValidConcern(true);
    };

    const clearAll = () => {
        setEmail(cookie.UserEmail || '');
        type === 'billing' && setOrderNo('');
        setFirstName(cookie.UserName || '');
        setLastName(cookie.LastName || '');
        setPhone('');
        setConcern('');
        setIsValidAll(false);
        setIsValidEmail(true);
        setIsValidOrderNo(true);
        setIsValidFirstName(true);
        setIsValidLastName(true);
        setIsValidPhone(true);
        setIsValidConcern(true);
    };

    // Web to Case function
    function webToCase(fields) {
        const customHiddenIframeName = 'WEBTOCASE_API';
        if (!document.getElementById(customHiddenIframeName)) {
            const theiFrame = document.createElement('iframe');
            theiFrame.id = customHiddenIframeName;
            theiFrame.name = customHiddenIframeName;
            theiFrame.src = 'about:blank';
            theiFrame.style.display = 'none';
            document.body.appendChild(theiFrame);
        }
        fields['retURL'] = window.origin;
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = window.salesForceWebToCaseURL;
        form.setAttribute('target', customHiddenIframeName);
        for (const fieldName in fields) {
            const theInput = document.createElement('input');
            theInput.name = fieldName;
            theInput.value = fields[fieldName];
            theInput.setAttribute('type', 'hidden');
            form.appendChild(theInput);
        }
        document.body.appendChild(form);
        form.submit();
    }

    const submitEmailService = async () => {
        const _trackingUrl = window.location.href;
        const _currentUrl = new URL(_trackingUrl.toLowerCase());
        const _curURLSourceAriticle = _currentUrl.searchParams.get('sourcearticle') || '';
        const _stateText = '';
        const _subject = 'CCC_Web-to-case_' + email + '_' + _stateText + '_Article_' + _curURLSourceAriticle;
        const _name = firstName + ' ' + lastName;
        const _orderNumberParam = window.orderNumberParam;
        const data = {
            orgid: window.orgId,
            retURL: window.location.origin,
            priority: 'High',
            name: _name,
            email: email,
            phone: phone,
            subject: _subject,
            description: concern,
            [_orderNumberParam]: orderNo,
        };
        webToCase(data);
    };

    const submitEmail = async () => {
        try {
            await submitEmailService();
            setIsSubmitSuccess(true);
            setStep('after submit');
        } catch (error) {
            setIsSubmitSuccess(false);
            setStep('after submit');
            console.log(error);
        } finally {
            setIsSubmitClicked(false);
            setStep('after submit');
            clearAll();
            console.log('form send');
        }
    };

    const updateIsValidAll = () => {
        setIsValidAll(true);

        let validAll = true;

        if (!getIsEmailValid()) {
            setIsValidEmail(false);
            setIsValidAll(false);
            validAll = false;
        }

        if (type === 'billing') {
            if (!getIsOrderNoValid()) {
                setIsValidOrderNo(false);
                setIsValidAll(false);
                validAll = false;
            }
        }

        if (!getIsFirstNameValid()) {
            setIsValidFirstName(false);
            setIsValidAll(false);
            validAll = false;
        }

        if (!getIsLastNameValid()) {
            setIsValidLastName(false);
            setIsValidAll(false);
            validAll = false;
        }

        if (!getIsPhoneValid()) {
            setIsValidPhone(false);
            setIsValidAll(false);
            validAll = false;
        }

        if (!getIsConcernValid()) {
            setIsValidConcern(false);
            setIsValidAll(false);
            validAll = false;
        }

        setIsValidAll(validAll);
        return validAll;
    };

    const handleSubmit = () => {
        setIsSubmitClicked(true);
    };

    useEffect(() => {
        if (isSubmitClicked) {
            updateIsValidAll() ? submitEmail() : console.log('form invalid');
            setIsSubmitClicked(false);
        }
    }, [isSubmitClicked]);

    useEffect(() => {
        clearAll();
    }, []);

    return (
        <section className="tsc-contact-form">
            <h1 className="tsc-contact-form__title">{title}</h1>

            {/*Section After Submit Success Begins */}
            {step == 'after submit' && isSubmitSuccess && (
                <div className="tsc-contact-form__submit-messsage">
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('submissionReceived')}
                        {'.'}
                    </p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('weWillSendYouAnEmail')}
                        {'.*'}
                    </p>
                    <p className="tsc-contact-form__submit-messsage__p">{t('weAimToGetBack')}</p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('theTscCustomerCareTeam')}
                        {'.'}
                    </p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('pleaseClickOnTheFollowingLinkTo')} <a href="/?ic=web-to-case">{t('continueShopping')}</a>
                        {'.'}
                    </p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('ifYouHaventReceievedOurEmail')}
                        {'.'}
                    </p>
                </div>
            )}

            {/*Section After Submit Success Ends */}
            {/*Section After Submit Error Begins */}
            {step == 'after submit' && !isSubmitSuccess && (
                <div className="tsc-contact-form__submit-messsage tsc-contact-form__submit-messsage--error">
                    <p className="tsc-contact-form__submit-messsage__p">{t('somethingWentWrongOnOurEnd')}</p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('pleaseSendUsYourConcerns')} <a href="tel:18882020888">{'1-888-2020-888'}</a>
                    </p>
                    <p className="tsc-contact-form__submit-messsage__p">{t('thankYou')}</p>
                    <p className="tsc-contact-form__submit-messsage__p">{t('theTscCustomerCareTeam')}</p>
                    <p className="tsc-contact-form__submit-messsage__p">
                        {t('pleaseClickOnTheFollowingLinkTo')} <a href="/?ic=web-to-case">{t('continueShopping')}</a>
                        {'.'}
                    </p>
                </div>
            )}

            <div className="tsc-contact-form__text">
                <div>{subtitle}</div>
            </div>
            <span className="tsc-contact-form__message">{t('required')}</span>
            <div className="tsc-contact-form__form">
                {/* Email */}
                <div className="tsc-contact-form__form__label">
                    <input
                        className="tsc-contact-form__form__label__input"
                        data-valid={isValidEmail}
                        data-empty={email !== '' ? false : true}
                        type="text"
                        placeholder=""
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required={true}
                        onBlur={onEmailAddressBlur}
                        onFocus={onEmailAddressFocus}
                    />
                    <span className="tsc-contact-form__form__label__placeholder">{t('emailAddress')}</span>
                    {!isValidEmail && (
                        <div className="tsc-contact-form__form__label__validation">
                            {t('pleaseEnterYourEmailAddressInTheFormat')}
                        </div>
                    )}
                    <span className="tsc-contact-form__form__label__text">{t('emailAddressInfo')}</span>
                </div>
                {/* Order Number */}
                {type === 'billing' && (
                    <div className="tsc-contact-form__form__label">
                        <input
                            className="tsc-contact-form__form__label__input"
                            data-valid={isValidOrderNo}
                            data-empty={orderNo !== '' ? false : true}
                            type="text"
                            placeholder=""
                            value={orderNo}
                            onChange={(e) => setOrderNo(e.target.value)}
                            required={true}
                            onBlur={onOrderNoBlur}
                            onFocus={onOrderNoFocus}
                        />
                        <span className="tsc-contact-form__form__label__placeholder">{t('orderNumber')}</span>
                        {!isValidOrderNo && (
                            <div className="tsc-contact-form__form__label__validation">
                                {t('validOrderNumberIsRequired')}
                            </div>
                        )}
                    </div>
                )}
                {/* Firstname */}
                <div className="tsc-contact-form__form__label">
                    <input
                        className="tsc-contact-form__form__label__input"
                        data-valid={isValidFirstName}
                        data-empty={firstName !== '' ? false : true}
                        type="text"
                        placeholder=""
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required={true}
                        onBlur={onFirstNameBlur}
                        onFocus={onFirstNameFocus}
                    />
                    <span className="tsc-contact-form__form__label__placeholder">{t('firstName')}</span>
                    {!isValidFirstName && (
                        <div className="tsc-contact-form__form__label__validation">{t('firstNameValidation')}</div>
                    )}
                </div>
                {/* Lastname */}
                <div className="tsc-contact-form__form__label">
                    <input
                        className="tsc-contact-form__form__label__input"
                        data-valid={isValidLastName}
                        data-empty={lastName !== '' ? false : true}
                        type="text"
                        placeholder=""
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required={true}
                        onBlur={onLastNameBlur}
                        onFocus={onLastNameFocus}
                    />
                    <span className="tsc-contact-form__form__label__placeholder">{t('lastName')}</span>
                    {!isValidLastName && (
                        <div className="tsc-contact-form__form__label__validation">{t('lastNameValidation')}</div>
                    )}
                </div>
                {/* Phone */}
                <div className="tsc-contact-form__form__label">
                    <MaskdInput
                        mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        className="tsc-contact-form__form__label__input"
                        data-valid={isValidPhone}
                        data-empty={phone !== '' ? false : true}
                        guide={false}
                        type="text"
                        value={phone}
                        onBlur={onPhoneBlur}
                        onFocus={onPhoneFocus}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                    <span className="tsc-contact-form__form__label__placeholder">{t('phoneNumber')}</span>
                    {!isValidPhone && (
                        <div className="tsc-contact-form__form__label__validation">{t('phoneValidation')}</div>
                    )}
                </div>
                {/* Concern */}
                <div className="tsc-contact-form__form__label">
                    <textarea
                        className="tsc-contact-form__form__label__textarea"
                        data-valid={isValidConcern}
                        data-empty={concern !== '' ? false : true}
                        placeholder=""
                        value={concern}
                        onChange={(e) => setConcern(e.target.value)}
                        required={true}
                        onBlur={onConcernBlur}
                        onFocus={onConcernFocus}
                    />
                    <span className="tsc-contact-form__form__label__placeholder--textarea">{t('concerns')}</span>
                    {!isValidConcern && (
                        <div className="tsc-contact-form__form__label__validation">{t('concernValidation')}</div>
                    )}
                </div>
                <button className="tsc-contact-form__form__submit-button" onClick={() => handleSubmit()}>
                    {t('submit')}
                </button>
            </div>
            <p className="tsc-contact-form__information">
                {t('contactFormInformation1')}{' '}
                <button className="tsc-contact-form__button-link__live-chat" onClick={openHelpPanel}>
                    {t('contactFormInformation2')}
                </button>{' '}
                {t('contactFormInformation3')} <strong>1-888-2020-888</strong> {t('contactFormInformation4')}
            </p>
        </section>
    );
};

export default ContactForm;
